import { FetchBuilderOptions, FetchFnArgs } from '../types/apiTypes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../contexts/UserContext';
import { useEffect, useMemo } from 'react';
import { BASE_URL, BASE_URL_MAP } from '../lib/environment';

const scope = 'RDPs';

const fetchHospitalityRDPs = async (
  args: FetchFnArgs
): Promise<OperatorSummary[]> => {
  const params = buildParams(args);
  const response = await fetch(
    `${BASE_URL_MAP['Hospitality']}/RDP`,
    params.options
  );

  try {
    const rdps = await response.json();
    return (rdps ?? []).map(mapHospitalityProfile);
  } catch (e) {
    console.error(e);
    return [];
  }
};

const fetchResidentialRDPs = async (
  args: FetchFnArgs
): Promise<RDPProfile[]> => {
  const params = buildParams(args);
  const response = await fetch(
    `${BASE_URL_MAP['Residential']}/RDP`,
    params.options
  );

  try {
    const rdps = await response.json();
    return (rdps ?? []).map(mapResidentialProfile);
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const useHosRDPs = () => {
  const queryClient = useQueryClient();
  const user = useUser();

  useEffect(() => {
    if (!user.loggedIn) queryClient.removeQueries([{ scope }]);
  }, [user.loggedIn]);

  return useQuery({
    queryKey: [{ scope, entity: 'Hospitality' }],
    queryFn: ({ signal }) =>
      fetchHospitalityRDPs({
        signal,
        access_token: user.token,
        baseUrl: BASE_URL,
      }),
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    enabled:
      user.permissions.canHos &&
      user.permissions.canHosRDPView &&
      user.loggedIn,
  });
};

export const useResRDPs = () => {
  const queryClient = useQueryClient();
  const user = useUser();

  useEffect(() => {
    if (!user.loggedIn) queryClient.removeQueries([{ scope }]);
  }, [user.loggedIn]);

  return useQuery({
    queryKey: [{ scope, entity: 'Residential' }],
    queryFn: ({ signal }) =>
      fetchResidentialRDPs({
        signal,
        access_token: user.token,
        baseUrl: BASE_URL,
      }),
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    enabled:
      user.permissions.canRes &&
      user.permissions.canResRDPView &&
      user.loggedIn,
  });
};

export const useRDPs = () => {
  const hospitality = useHosRDPs();
  const residential = useResRDPs();

  const rdps = useMemo(() => {
    return [...(hospitality.data ?? []), ...(residential.data ?? [])];
  }, [hospitality.data, residential.data]);

  return {
    isLoading: hospitality.isFetching || residential.isFetching,
    data: rdps,
  };
};

const buildParams = ({
  signal,
  access_token,
}: Omit<FetchFnArgs, 'operator'>): FetchBuilderOptions => {
  return {
    BASE_URL: '',
    options: {
      method: 'GET',
      headers: {
        Authorization: access_token ?? '',
      },
      signal,
    },
  };
};

const mapResidentialProfile = (profile: ApiRDPProfile) => ({
  code: profile.Code,
  name: profile.Name,
  type: 'Residential',
});

const mapHospitalityProfile = (profile: ApiRDPProfile) => ({
  code: profile.Code,
  name: profile.Name,
  type: 'Hospitality',
});

type ApiRDPProfile = {
  Code: string;
  Name: string;
  Type: OperatorType;
};

export type RDPProfile = ApiToPortalTranslator<ApiRDPProfile>;
